import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Grid3X3, Trash2, Plus, Edit, X, ArrowUpDown, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AdminTiles = () => {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTile, setEditingTile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    filter_type: 'budget',
    filter_value: '',
    display_order: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTiles();
  }, []);

  const fetchTiles = async () => {
    try {
      const response = await axios.get(`${API}/tiles`);
      setTiles(response.data);
    } catch (error) {
      console.error('Error fetching tiles:', error);
      toast.error('Failed to load tiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingTile) {
        await axios.put(`${API}/tiles/${editingTile.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tile updated successfully!');
      } else {
        await axios.post(`${API}/tiles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tile added successfully!');
      }
      
      setShowForm(false);
      setEditingTile(null);
      setFormData({ title: '', image_url: '', filter_type: 'budget', filter_value: '', display_order: 0 });
      fetchTiles();
    } catch (error) {
      console.error('Error saving tile:', error);
      toast.error('Failed to save tile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (tile) => {
    setEditingTile(tile);
    setFormData({
      title: tile.title,
      image_url: tile.image_url,
      filter_type: tile.filter_type,
      filter_value: tile.filter_value,
      display_order: tile.display_order || 0,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tile?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/tiles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tile deleted successfully!');
      fetchTiles();
    } catch (error) {
      console.error('Error deleting tile:', error);
      toast.error('Failed to delete tile');
    }
  };

  const handleAddNew = () => {
    setEditingTile(null);
    setFormData({ title: '', image_url: '', filter_type: 'budget', filter_value: '', display_order: tiles.length });
    setShowForm(true);
  };

  const getFilterTypeLabel = (type) => {
    const labels = {
      budget: 'Budget (Price)',
      bodyType: 'Body Type',
      transmission: 'Transmission',
      fuelType: 'Fuel Type',
      ownership: 'Ownership',
      hotDeal: 'Hot Deals'
    };
    return labels[type] || type;
  };

  const getFilterValueDisplay = (type, value) => {
    if (type === 'budget') {
      const price = parseInt(value);
      if (price >= 100000) return `Under ₹${(price / 100000).toFixed(0)} Lakh`;
      return `Under ₹${price.toLocaleString()}`;
    }
    if (type === 'ownership') {
      return `${value} Owner`;
    }
    if (type === 'hotDeal') {
      return 'All Hot Deals';
    }
    return value;
  };

  return (
    <div className="space-y-6" data-testid="admin-tiles">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
            <Grid3X3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="font-outfit font-bold text-2xl text-gray-900">Homepage Tiles</h2>
            <p className="font-dmsans text-sm text-gray-500">{tiles.length} tiles configured</p>
          </div>
        </div>
        
        <button
          onClick={handleAddNew}
          className="inline-flex items-center gap-2 px-5 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          data-testid="add-tile-button"
        >
          <Plus className="h-5 w-5" />
          Add Tile
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <p className="text-sm text-purple-800">
          <strong>Tiles</strong> appear in the "Find Your Perfect Match" section on the homepage. 
          Each tile links to filtered inventory based on the filter type and value you set.
        </p>
      </div>

      {/* Tiles Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      ) : tiles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Grid3X3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No tiles yet</p>
          <p className="text-sm text-gray-500 mt-1">Add tiles to customize the homepage</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tiles-grid">
          {tiles.map((tile, index) => (
            <div
              key={tile.id}
              className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
              data-testid={`tile-item-${tile.id}`}
            >
              <div className="aspect-video relative">
                <img
                  src={tile.image_url}
                  alt={tile.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e5e7eb" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dominant-baseline="middle" fill="%239ca3af" font-size="12">No Image</text></svg>';
                  }}
                />
                <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full font-medium flex items-center gap-1">
                  <ArrowUpDown className="h-3 w-3" />
                  Order: {tile.display_order}
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 text-lg mb-2">{tile.title}</h3>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                    {getFilterTypeLabel(tile.filter_type)}
                  </span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {getFilterValueDisplay(tile.filter_type, tile.filter_value)}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(tile)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                    data-testid={`edit-tile-${tile.id}`}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(tile.id)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center"
                    data-testid={`delete-tile-${tile.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="tile-form-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="font-bold text-xl text-gray-900">
                {editingTile ? 'Edit Tile' : 'Add New Tile'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingTile(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Tile Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Under ₹3 Lakh, SUVs, Automatic Cars"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="tile-title-input"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  placeholder="https://images.unsplash.com/... or Google Drive link"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="tile-image-input"
                />
                <p className="text-xs text-gray-500 mt-1">Supports direct URLs and Google Drive links (auto-converted)</p>
                {formData.image_url && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Filter Type */}
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Filter Type</label>
                <select
                  value={formData.filter_type}
                  onChange={(e) => setFormData({ ...formData, filter_type: e.target.value, filter_value: e.target.value === 'hotDeal' ? 'true' : '' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="tile-filter-type"
                >
                  <option value="budget">Budget (Max Price)</option>
                  <option value="bodyType">Body Type</option>
                  <option value="transmission">Transmission</option>
                  <option value="fuelType">Fuel Type</option>
                  <option value="ownership">Ownership</option>
                  <option value="hotDeal">🔥 Hot Deals</option>
                </select>
              </div>

              {/* Filter Value - Dynamic based on type */}
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Filter Value</label>
                {formData.filter_type === 'budget' && (
                  <select
                    value={formData.filter_value}
                    onChange={(e) => setFormData({ ...formData, filter_value: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="tile-filter-value"
                  >
                    <option value="">Select Max Budget</option>
                    <option value="200000">₹2 Lakh</option>
                    <option value="300000">₹3 Lakh</option>
                    <option value="500000">₹5 Lakh</option>
                    <option value="700000">₹7 Lakh</option>
                    <option value="1000000">₹10 Lakh</option>
                    <option value="1500000">₹15 Lakh</option>
                    <option value="2000000">₹20 Lakh</option>
                    <option value="3000000">₹30 Lakh</option>
                  </select>
                )}
                {formData.filter_type === 'bodyType' && (
                  <select
                    value={formData.filter_value}
                    onChange={(e) => setFormData({ ...formData, filter_value: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="tile-filter-value"
                  >
                    <option value="">Select Body Type</option>
                    <option value="Hatchback">Hatchback</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="MUV">MUV</option>
                    <option value="Coupe">Coupe</option>
                    <option value="Convertible">Convertible</option>
                  </select>
                )}
                {formData.filter_type === 'transmission' && (
                  <select
                    value={formData.filter_value}
                    onChange={(e) => setFormData({ ...formData, filter_value: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="tile-filter-value"
                  >
                    <option value="">Select Transmission</option>
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic</option>
                  </select>
                )}
                {formData.filter_type === 'fuelType' && (
                  <select
                    value={formData.filter_value}
                    onChange={(e) => setFormData({ ...formData, filter_value: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="tile-filter-value"
                  >
                    <option value="">Select Fuel Type</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="CNG">CNG</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                )}
                {formData.filter_type === 'ownership' && (
                  <select
                    value={formData.filter_value}
                    onChange={(e) => setFormData({ ...formData, filter_value: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="tile-filter-value"
                  >
                    <option value="">Select Ownership</option>
                    <option value="1">1st Owner</option>
                    <option value="2">2nd Owner</option>
                    <option value="3">3rd Owner</option>
                  </select>
                )}
                {formData.filter_type === 'hotDeal' && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
                    <span className="text-2xl">🔥</span>
                    <div>
                      <p className="font-medium text-orange-800">Hot Deals Filter</p>
                      <p className="text-sm text-orange-600">This tile will show all cars marked as Hot Deals</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Display Order */}
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="tile-order-input"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-tile-button"
                >
                  {submitting ? 'Saving...' : (editingTile ? 'Update Tile' : 'Add Tile')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTile(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
